import { supabase } from './supabase'

/**
 * Credit Service for managing AI generation credits
 */
class CreditService {
  /**
   * Get user's current credit balance and plan info
   */
  async getUserCredits(userId) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('project_credits, image_credits, role, beta_user, last_quota_renewal')
        .eq('id', userId)
        .single()

      if (error) throw error

      // Check and renew quota if needed
      await this.checkAndRenewQuota(userId)

      // Get fresh data after potential renewal
      const { data: freshData, error: freshError } = await supabase
        .from('users')
        .select('project_credits, image_credits, role, beta_user')
        .eq('id', userId)
        .single()

      if (freshError) throw freshError

      const planQuotas = this.getPlanQuotas(freshData.role, freshData.beta_user)

      return {
        projectCredits: freshData.project_credits || 0,
        imageCredits: freshData.image_credits || 0,
        planQuotas,
        plan: freshData.beta_user ? 'beta' : freshData.role
      }
    } catch (error) {
      console.error('Error fetching user credits:', error)
      throw error
    }
  }

  /**
   * Get plan quotas based on role and beta status
   */
  getPlanQuotas(role, isBeta) {
    if (isBeta) {
      return { project: 20, image: 200 }
    }
    
    switch (role) {
      case 'pro':
        return { project: 20, image: 200 }
      case 'free':
      default:
        return { project: 2, image: 20 }
    }
  }

  /**
   * Check if user has enough credits
   */
  async checkCredits(userId, creditType, amount = 1) {
    try {
      const { data, error } = await supabase.rpc('check_user_credits', {
        user_id: userId,
        credit_type_param: creditType,
        amount_needed: amount
      })

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error checking credits:', error)
      return false
    }
  }

  /**
   * Consume credits atomically
   */
  async consumeCredits(userId, creditType, amount = 1, description = null, projectId = null) {
    try {
      const { data, error } = await supabase.rpc('consume_credits', {
        user_id: userId,
        credit_type_param: creditType,
        amount,
        description_param: description,
        project_id_param: projectId
      })

      if (error) throw error
      return data // Returns true if successful, false if insufficient credits
    } catch (error) {
      console.error('Error consuming credits:', error)
      return false
    }
  }

  /**
   * Grant credits to user (admin function)
   */
  async grantCredits(userId, creditType, amount, source = 'admin_grant', description = null) {
    try {
      const { error } = await supabase.rpc('grant_credits', {
        user_id: userId,
        credit_type_param: creditType,
        amount,
        source_param: source,
        description_param: description
      })

      if (error) throw error
      return true
    } catch (error) {
      console.error('Error granting credits:', error)
      return false
    }
  }

  /**
   * Get user's credit transaction history
   */
  async getCreditHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching credit history:', error)
      return []
    }
  }

  /**
   * Check and renew quota if needed
   */
  async checkAndRenewQuota(userId) {
    try {
      const { data, error } = await supabase.rpc('check_and_renew_quota', {
        user_id: userId
      })

      if (error) throw error
      return data // Returns true if renewal was performed
    } catch (error) {
      console.error('Error checking/renewing quota:', error)
      return false
    }
  }

  /**
   * Force quota renewal (useful after plan upgrades)
   */
  async forceQuotaRenewal(userId) {
    try {
      // Get user's current plan info
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role, beta_user')
        .eq('id', userId)
        .single()

      if (userError) throw userError

      const planQuotas = this.getPlanQuotas(userData.role, userData.beta_user)

      // Update user's credits to match their plan quotas
      const { error: updateError } = await supabase
        .from('users')
        .update({
          project_credits: planQuotas.project,
          image_credits: planQuotas.image,
          last_quota_renewal: new Date(),
          updated_at: new Date()
        })
        .eq('id', userId)

      if (updateError) throw updateError

      console.log('✅ Forced quota renewal for user:', userId, 'New quotas:', planQuotas)
      return true
    } catch (error) {
      console.error('Error forcing quota renewal:', error)
      return false
    }
  }

  /**
   * Format credit type for display
   */
  formatCreditType(creditType) {
    switch (creditType) {
      case 'project':
        return 'AI Project Generation'
      case 'image':
        return 'AI Image Generation'
      default:
        return creditType
    }
  }

  /**
   * Format credit source for display
   */
  formatCreditSource(source) {
    switch (source) {
      case 'monthly_quota':
        return 'Monthly Quota'
      case 'purchase':
        return 'Purchase'
      case 'admin_grant':
        return 'Admin Grant'
      case 'bonus':
        return 'Bonus'
      default:
        return source
    }
  }

  /**
   * Get credit usage summary for current month
   */
  async getMonthlyUsage(userId) {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7) // YYYY-MM format
      
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('credit_type, amount')
        .eq('user_id', userId)
        .gte('created_at', `${currentMonth}-01`)
        .lt('amount', 0) // Only consumption (negative amounts)

      if (error) throw error

      const usage = {
        project: 0,
        image: 0
      }

      data?.forEach(transaction => {
        usage[transaction.credit_type] += Math.abs(transaction.amount)
      })

      return usage
    } catch (error) {
      console.error('Error fetching monthly usage:', error)
      return { project: 0, image: 0 }
    }
  }

  /**
   * Check if user can perform an action based on credits
   */
  async canPerformAction(userId, action) {
    const actionCosts = {
      'generate_project': { type: 'project', amount: 1 },
      'generate_image': { type: 'image', amount: 1 }
    }

    const cost = actionCosts[action]
    if (!cost) return true

    return await this.checkCredits(userId, cost.type, cost.amount)
  }
}

export const creditService = new CreditService()
export default creditService
